import React, {
    useCallback,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { auctioneerDataTemplatePropertiesType, auctioneerDataTemplateType } from '../../types';
import useExtendedState from '../../hooks/useExtendedState';
import ExpandedListTab from '../ExpandedListTab';
import ReadonlyField from '../fields/ReadonlyField';
import AuctioneerDataMatrix from '../auctioneer-data-matrix/AuctioneerDataMatrix';
import Privileges from '../../utils/Privileges';
import PrivilegedTextField from '../fields/privileged/PrivilegedTextField';
import useMultiValidator from '../../hooks/useMultiValidator';
import {
    AUCTIONEER_DATA_TEMPLATE_NAME_MAX_LETTERS,
    auctioneerDataTemplateItemsValidator,
    auctioneerDataTemplateNameValidator,
} from '../../validators/auctioneerDataTemplates';
import { Privileged } from '../../hocs/withPrivileges';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import {
    ALL_AUCTIONEER_DATA_TEMPLATES_QUERY_NAME,
    DELETE_AUCTIONEER_DATA_TEMPLATE,
    UPDATE_AUCTIONEER_DATA_TEMPLATE,
} from '../../graphql/auctioneerDataTemplates';
import Constants from '../../utils/Constants';
import DeleteButton from '../buttons/DeleteButton';
import DownloadButton from '../buttons/DownloadButton';
import UploadButton from '../buttons/UploadButton';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import DownloadSampleFile from '../DownloadSampleFile';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

AuctioneerDataTemplateDetailTab.propTypes = {
    auctioneerDataTemplate: auctioneerDataTemplateType.isRequired,
    auctioneerDataTemplateProps: auctioneerDataTemplatePropertiesType.isRequired,
    onDetailDelete: PropTypes.func.isRequired,
    onUpload: PropTypes.func.isRequired,
};

function AuctioneerDataTemplateDetailTab(props) {
    const {
        auctioneerDataTemplate,
        auctioneerDataTemplateProps,
        onDetailDelete,
        onUpload,
    } = props;
    const {
        availableAuctioneers,
        availableSubmarkets,
        maxQuantitySum,
    } = auctioneerDataTemplateProps;
    const { t } = useTranslation();
    const [resetComponentsAfterModification, setResetComponentsAfterModification] = useState(false);
    const [adjustValuesAfterExternalUpdate, setAdjustValuesAfterExternalUpdate] = useState(false);
    const nameState = useExtendedState(auctioneerDataTemplate.name);
    const templateItemsState = useExtendedState([...auctioneerDataTemplate.templateItems]);
    const onTemplateItemsChange = (newTemplateItems) => {
        setResetComponentsAfterModification(false);
        setAdjustValuesAfterExternalUpdate(false);
        templateItemsState.setValue(newTemplateItems);
    };
    const { validators, isValid } = useMultiValidator(true, {
        name: compositeValidator(auctioneerDataTemplateNameValidator(t), wafValidator(t)),
        templateItems: auctioneerDataTemplateItemsValidator(t, Constants.MIN_AUCTIONEER_DATA_QUANTITY, maxQuantitySum),
    });
    const isChangedPromise = nameState.isChanged || templateItemsState.isChanged ? Promise.resolve() : Promise.reject();
    const [isDisabled, setIsDisabled] = useState(true);
    Promise.all([isChangedPromise, isValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const updateAuctioneerDataTemplateMutation = useExtendedMutation(UPDATE_AUCTIONEER_DATA_TEMPLATE, {
        variables: {
            id: auctioneerDataTemplate.id,
            name: nameState.value,
            templateItems: templateItemsState.value.map((templateItem) => ({
                memberId: templateItem.auctioneer.memberId,
                submarketCode: templateItem.submarket.code,
                quantity: templateItem.quantity,
            })),
        },
        successCallback: () => setResetComponentsAfterModification(true),
        statesToResetOnSuccess: [nameState, templateItemsState],
        successMessageOptions: { key: 'auctioneer-data-templates.notification.auctioneer-data-template-update-success', dataProperty: 'updateAuctioneerDataTemplate' },
        failureMessageOptions: { key: 'auctioneer-data-templates.notification.auctioneer-data-template-update-failure', params: { name: auctioneerDataTemplate.name } },
        refetchQueries: [ALL_AUCTIONEER_DATA_TEMPLATES_QUERY_NAME],
    });
    const deleteAuctioneerDataTemplateMutation = useExtendedMutation(DELETE_AUCTIONEER_DATA_TEMPLATE, {
        variables: { id: auctioneerDataTemplate.id },
        successCallback: onDetailDelete,
        successMessageOptions: { key: 'auctioneer-data-templates.notification.auctioneer-data-template-delete-success', dataProperty: 'deleteAuctioneerDataTemplate' },
        failureMessageOptions: { key: 'auctioneer-data-templates.notification.auctioneer-data-template-delete-failure', params: { name: auctioneerDataTemplate.name } },
        refetchQueries: [ALL_AUCTIONEER_DATA_TEMPLATES_QUERY_NAME],
    });
    const downloadData = {
        id: auctioneerDataTemplate.id,
    };
    const downloadErrorMessageKeys = {
        unauthorized: 'auctioneer-data-templates.download-failed-unauthorized',
        failed: 'auctioneer-data-templates.download-failed',
    };
    const onSuccessUpload = useCallback(() => {
        onUpload();
        setAdjustValuesAfterExternalUpdate(true);
    }, [onUpload]);
    useEffectOnUpdate(() => {
        templateItemsState.changeInitialState([...auctioneerDataTemplate.templateItems]);
        templateItemsState.reset();
    }, [auctioneerDataTemplate]);
    return (
        <ExpandedListTab
            className="auctioneer-data-template-detail-tab"
            title={t('auctioneer-data-templates.auctioneer-data-template-detail')}
        >
            <div className="rich-list-row-expanded--fields">
                <ReadonlyField
                    label={t('auctioneer-data-templates.id')}
                    value={auctioneerDataTemplate.id}
                    className="auctioneer-data-template-detail-tab--fields--id"
                />
                <PrivilegedTextField
                    sufficientPrivileges={[Privileges.AUCTIONEER_DATA_TEMPLATE_EDIT]}
                    label={t('auctioneer-data-templates.name')}
                    state={nameState}
                    validator={validators.name}
                    className="auctioneer-data-template-detail-tab--fields--name wide"
                    inputId="auctioneer-data-template-detail-tab--fields--name"
                    placeholder={t('auctioneer-data-templates.placeholder.name')}
                    maxLength={AUCTIONEER_DATA_TEMPLATE_NAME_MAX_LETTERS}
                    isRequired
                />
            </div>
            <div className="auctioneer-data-template-detail--auctioneer-data-template-matrix">
                <AuctioneerDataMatrix
                    availableAuctioneers={availableAuctioneers}
                    availableSubmarkets={availableSubmarkets}
                    maxQuantitySum={maxQuantitySum}
                    items={templateItemsState.value}
                    validator={validators.templateItems}
                    onItemsChange={onTemplateItemsChange}
                    onReset={templateItemsState.reset}
                    isChanged={templateItemsState.isChanged}
                    doResetComponentsAfterModification={resetComponentsAfterModification}
                    doAdjustValuesAfterExternalUpdate={adjustValuesAfterExternalUpdate}
                    isRequired
                />
            </div>
            <Privileged sufficientPrivileges={[Privileges.AUCTIONEER_DATA_TEMPLATE_EDIT]}>
                <div className="rich-list-row-expanded--buttons">
                    <div className="rich-list-row-expanded--buttons--left">
                        <ApplyChangesButton
                            extendedMutation={updateAuctioneerDataTemplateMutation}
                            disabled={isDisabled}
                            confirmationMessage={t('auctioneer-data-templates.confirm-update-auctioneer-data-template', { name: auctioneerDataTemplate.name })}
                        />
                        <DownloadButton
                            urlPath={Constants.DOWNLOAD_AUCTIONEER_DATA_TEMPLATE_PATH}
                            data={downloadData}
                            errorMessageKeys={downloadErrorMessageKeys}
                        />
                        <UploadButton
                            label={t('button.upload')}
                            className="button-bordered update-matrix-upload-button"
                            uploadPath={Constants.UPLOAD_AUCTIONEER_DATA_TEMPLATE_PATH}
                            accept=".xls,.xlsx"
                            onUpload={onSuccessUpload}
                            baseVariables={[{
                                name: 'id',
                                value: auctioneerDataTemplate.id,
                            }]}
                        >
                            <DownloadSampleFile
                                text={t('auctioneer-data-templates.download-sample-file-text')}
                                fileName="sampleAuctioneerMatrixUploadFile"
                                sampleType="AUCTIONEER_MATRIX"
                                fileTypes={['XLSX']}
                            />
                        </UploadButton>
                    </div>
                    <div className="rich-list-row-expanded--buttons--right">
                        <DeleteButton
                            extendedMutation={deleteAuctioneerDataTemplateMutation}
                            confirmationMessage={t('auctioneer-data-templates.confirm-delete-auctioneer-data-template', { name: auctioneerDataTemplate.name })}
                        />
                    </div>
                </div>
            </Privileged>
        </ExpandedListTab>
    );
}

export default AuctioneerDataTemplateDetailTab;
